export interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: ArticleSection[];
  category: string;
  categorySlug: string;
  coverImage: string;
  author: string;
  publishDate: string;
  readTime: string;
  tags: string[];
  featured: boolean;
  hot: boolean;
}

export interface ArticleSection {
  type: 'heading' | 'paragraph' | 'image' | 'list';
  level?: number;
  content?: string;
  items?: string[];
  imageUrl?: string;
  imageAlt?: string;
}

export const categories = [
  { name: '日常换装', slug: 'daily', icon: 'ri-t-shirt-line', description: '日常生活中的快速换装技巧与实用方法' },
  { name: '穿搭搭配', slug: 'match', icon: 'ri-palette-line', description: '从色彩到风格的完整穿搭搭配指南' },
  { name: '脱衣顺序', slug: 'order', icon: 'ri-arrow-down-line', description: '正确的脱衣顺序与衣物保养知识' },
  { name: '收纳整理', slug: 'storage', icon: 'ri-folders-line', description: '衣物收纳技巧与空间优化方案' },
  { name: '时尚技巧', slug: 'tips', icon: 'ri-sparkling-line', description: '时尚前沿趋势与穿搭灵感分享' },
];

export const articles: Article[] = [
  {
    id: '1',
    slug: '5-minute-daily-change',
    title: '日常换装技巧：5分钟快速出门穿搭法',
    excerpt: '掌握这些高效的日常换装技巧，让你每天早上只需5分钟就能完成从睡衣到精致出门装的完美切换，从此告别穿搭纠结症。',
    content: [
      { type: 'heading', level: 2, content: '为什么你需要高效换装技巧' },
      { type: 'paragraph', content: '对于现代女性来说，早晨的时间总是最宝贵的。调查显示，女性平均每天早上花费15-25分钟在挑选和更换衣物上。如果能将这个时间压缩到5分钟以内，你每周可以多出将近2小时的自由时间。本文将为你分享一套经过验证的高效换装方法，帮助你在最短时间内完成精致出门造型。' },
      { type: 'heading', level: 2, content: '第一步：提前规划是高效换装的关键' },
      { type: 'paragraph', content: '高效换装的核心不在于"脱得快"，而在于"有准备"。建议在前一天晚上花3分钟做好以下准备：查看第二天的天气预报；根据日程安排确定着装风格（职场、休闲、约会等）；提前将全套衣物挂在显眼位置；检查鞋子和配饰是否与服装搭配协调。' },
      { type: 'heading', level: 2, content: '第二步：掌握正确的脱衣顺序' },
      { type: 'paragraph', content: '很多人不知道，脱衣服的顺序直接影响换装效率。正确的顺序是：先脱外套或罩衫，然后脱上衣，接着脱下装，最后脱内衣。遵循"由外向内、由上至下"的原则，可以有效避免衣物缠绕，让整个换装过程更加流畅。' },
      { type: 'heading', level: 2, content: '第三步：分区域收纳，一秒定位' },
      { type: 'paragraph', content: '将衣柜按照"上衣区、下装区、外套区、配饰区"进行分区收纳。使用统一的衣架，同类型衣物按颜色深浅排列。这样当你需要拿取某件衣物时，可以在1秒内精准定位，大大节省翻找时间。' },
      { type: 'heading', level: 2, content: '第四步：善用"胶囊衣橱"理念' },
      { type: 'paragraph', content: '胶囊衣橱是指精选20-30件百搭单品，通过不同组合创造出多种穿搭方案。这些单品之间可以自由搭配，大大减少了选择困难。建议选择基础色系（黑、白、灰、米色）的单品作为核心，再加入2-3件亮色或图案单品增加活力。' },
      { type: 'heading', level: 2, content: '第五步：建立个人穿搭模板' },
      { type: 'paragraph', content: '找到3-5套最适合自己的固定搭配模板，例如"白衬衫+牛仔裤+小白鞋"、"针织衫+半身裙+短靴"等。将这些模板拍照存档，当不知道穿什么时，直接从模板库中选择即可，省去大量试穿和搭配的时间。' },
    ],
    category: '日常换装',
    categorySlug: 'daily',
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20wardrobe%20with%20neatly%20arranged%20clothing%20in%20warm%20beige%20and%20cream%20tones%2C%20soft%20morning%20light%20streaming%20through%20window%2C%20Scandinavian%20interior%20style%2C%20fashion%20lifestyle%20photography%2C%20clean%20and%20serene%20atmosphere%2C%20organized%20closet%20with%20wooden%20hangers%2C%20feminine%20aesthetic&width=800&height=500&seq=article-daily-01&orientation=landscape',
    author: '小美',
    publishDate: '2026-08-01',
    readTime: '6分钟',
    tags: ['快速换装', '日常穿搭', '胶囊衣橱', '衣柜收纳'],
    featured: true,
    hot: true,
  },
  {
    id: '2',
    slug: 'seasonal-clothes-storage',
    title: '换季衣物收纳全攻略：让你的衣橱焕然一新',
    excerpt: '换季时节到了，面对堆积如山的衣物不知道从何下手？这份保姆级收纳攻略将一步步教你整理换季衣物，让衣橱空间利用率提升3倍。',
    content: [
      { type: 'heading', level: 2, content: '换季收纳的黄金时机' },
      { type: 'paragraph', content: '每年3-4月和9-10月是换季收纳的最佳时机。春季将厚重冬装收纳，为轻薄的春夏装腾出空间；秋季则反过来，将夏装打包，让保暖的秋冬装登场。把握好这两个时间节点，能让你的衣橱始终保持整洁有序。' },
      { type: 'heading', level: 2, content: '收纳前的准备工作' },
      { type: 'paragraph', content: '在开始收纳之前，务必将所有要收纳的衣物彻底清洗干净。任何残留的污渍、汗渍在长期存放后都会变成难以去除的顽固污渍。洗净后确保衣物完全晾干，潮湿的衣物在密封环境中容易滋生霉菌，毁掉整批衣物。' },
      { type: 'heading', level: 2, content: '分类整理四步法' },
      { type: 'list', items: ['断舍离：将一年以上未穿、不合身、破损严重的衣物果断处理掉，可以捐赠、回收或丢弃', '按材质分：羊毛、丝绸等娇贵面料需要特殊收纳方式；棉麻、化纤类可以常规收纳', '按厚度分：厚外套、毛衣、薄衬衫、T恤等分类存放，方便来年取用', '按颜色分：浅色和深色分开存放，避免长期接触导致染色'] },
      { type: 'heading', level: 2, content: '不同衣物的收纳技巧' },
      { type: 'paragraph', content: '羽绒服和棉服建议使用透气的无纺布收纳袋，不要用真空压缩袋，否则会破坏羽绒的蓬松度。羊毛衫和羊绒衫应该折叠平放，悬挂会导致变形。丝绸类衣物需要用防酸纸包裹，避免与塑料直接接触。牛仔裤和厚棉布衣物可以用真空压缩袋最大限度节省空间。' },
      { type: 'heading', level: 2, content: '衣柜空间优化方案' },
      { type: 'paragraph', content: '充分利用衣柜的垂直空间：上层放置过季衣物收纳箱，中层挂当季常穿衣物，下层用抽屉或收纳盒存放内衣、袜子等小件。衣柜门内侧可以安装挂钩或收纳袋，用来挂围巾、腰带等配饰。床底空间也是绝佳的过季衣物存放点。' },
    ],
    category: '收纳整理',
    categorySlug: 'storage',
    coverImage: 'https://readdy.ai/api/search-image?query=Beautifully%20organized%20walk-in%20closet%20with%20seasonal%20clothing%20neatly%20folded%20and%20color%20coordinated%2C%20storage%20boxes%20on%20shelves%2C%20warm%20lighting%2C%20luxurious%20feminine%20wardrobe%20organization%2C%20minimalist%20aesthetic%2C%20cream%20and%20beige%20tones&width=800&height=500&seq=article-storage-02&orientation=landscape',
    author: '收纳达人莉莉',
    publishDate: '2026-07-28',
    readTime: '8分钟',
    tags: ['换季收纳', '衣柜整理', '空间优化', '衣物保养'],
    featured: true,
    hot: true,
  },
  {
    id: '3',
    slug: 'workplace-outfit-guide',
    title: '职场女性穿搭法则：从面试到日常的专业感穿搭',
    excerpt: '职场穿搭是女性形象管理的重要一环。本文从面试着装到日常办公穿搭，为您提供一套完整的职场穿搭体系，让专业感与时尚感并存。',
    content: [
      { type: 'heading', level: 2, content: '职场穿搭的底层逻辑' },
      { type: 'paragraph', content: '职场穿搭的核心是"得体"二字。不同的行业、职位、公司文化对着装有不同的要求。金融、法律等传统行业偏保守正式，互联网、创意行业则更加灵活自由。但无论什么行业，整洁、合身、有质感都是基本要求。' },
      { type: 'heading', level: 2, content: '面试着装：第一印象决定成败' },
      { type: 'paragraph', content: '面试是展示专业形象的关键时刻。建议选择一套剪裁精良的西装套装或西装连衣裙，颜色以深蓝、炭灰、黑色为佳。内搭选择白色或浅蓝色的衬衫，鞋子选择3-5厘米的黑色或裸色高跟鞋。妆容以自然淡雅为主，配饰尽量精简。记住：面试官在见到你的前7秒就已经形成了初步印象。' },
      { type: 'heading', level: 2, content: '日常办公的万能搭配公式' },
      { type: 'list', items: ['衬衫 + 西裤 + 尖头平底鞋：经典不挑人，适合大多数办公场合', '针织衫 + 半身裙 + 短靴：优雅知性，适合秋冬季节', '连衣裙 + 小西装外套：温柔有力量，开会见客户都合适', '高领打底衫 + 阔腿裤 + 乐福鞋：舒适有型，适合需要长时间坐着的岗位'] },
      { type: 'heading', level: 2, content: '职场配饰的选择艺术' },
      { type: 'paragraph', content: '职场配饰的原则是"少即是多"。一块简约的手表、一对小巧的耳钉、一条精致的细链项链，足以提升整体造型的质感。避免过于夸张的饰品，比如大圈耳环、多层手链等。包包选择容量适中的通勤包，颜色以黑色、棕色、米色为佳。' },
    ],
    category: '穿搭搭配',
    categorySlug: 'match',
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20professional%20woman%20in%20tailored%20blazer%20and%20trousers%2C%20modern%20office%20setting%20with%20natural%20light%2C%20confident%20pose%2C%20fashion%20editorial%20style%2C%20warm%20neutral%20tones%2C%20sophisticated%20workplace%20fashion%2C%20clean%20minimalist%20aesthetic&width=800&height=500&seq=article-workplace-03&orientation=landscape',
    author: '职场时尚编辑',
    publishDate: '2026-07-25',
    readTime: '7分钟',
    tags: ['职场穿搭', '面试着装', '办公室时尚', '形象管理'],
    featured: false,
    hot: true,
  },
  {
    id: '4',
    slug: 'correct-undressing-order',
    title: '正确的脱衣顺序：保护衣物与皮肤的秘诀',
    excerpt: '您知道吗？错误的脱衣顺序不仅会损坏衣物，还可能伤害皮肤和头发。掌握正确的脱衣顺序，让每一次换装都成为对衣物和身体的呵护。',
    content: [
      { type: 'heading', level: 2, content: '脱衣服也需要技巧' },
      { type: 'paragraph', content: '大多数人从未认真思考过脱衣服的顺序问题。实际上，正确的脱衣顺序与穿衣服的顺序一样重要。错误的脱衣方式会导致衣物拉拽变形、面料磨损，甚至在脱套头衫时拉扯到头发和皮肤，造成不必要的损伤。' },
      { type: 'heading', level: 2, content: '标准脱衣流程：从外到内，从上到下' },
      { type: 'paragraph', content: '推荐的标准脱衣顺序为：第一步，脱下外套、大衣等最外层衣物；第二步，脱去配饰（围巾、腰带、首饰等）；第三步，脱去上装（从外层的开衫到内层的打底衫）；第四步，脱下装（裤子或裙子）；第五步，脱鞋袜；最后一步，脱内衣。这个顺序遵循"由外向内、由上至下"的原则，能最大程度保护衣物和身体。' },
      { type: 'heading', level: 2, content: '特殊衣物的脱法技巧' },
      { type: 'list', items: ['套头衫：双手交叉抓住衣服下摆，从下往上拉起，这样不会拉扯到头发和妆容', '连衣裙：先拉开后背拉链或解开纽扣，从肩部将裙子往下褪，而非从下摆往上拉', '紧身裤/牛仔裤：坐着脱比站着脱更省力，先将裤子褪到膝盖以下，再逐条腿脱出', '丝袜：从腰部卷到脚尖再脱下，避免指甲勾丝'] },
      { type: 'heading', level: 2, content: '脱衣与衣物保养的关系' },
      { type: 'paragraph', content: '正确的脱衣方式能显著延长衣物的使用寿命。脱下衣物后，不要随手乱扔，应该立即挂好或叠好。尤其是西装、大衣等需要保持版型的衣物，脱下后应立即用宽肩衣架挂起。针织类衣物脱下后应平铺放置，避免悬挂导致变形。' },
    ],
    category: '脱衣顺序',
    categorySlug: 'order',
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20in%20a%20beautiful%20bedroom%20gently%20removing%20a%20silk%20scarf%2C%20soft%20afternoon%20light%2C%20luxurious%20interior%20with%20minimalist%20decor%2C%20feminine%20lifestyle%20photography%2C%20warm%20cream%20and%20beige%20tones%2C%20serene%20atmosphere&width=800&height=500&seq=article-order-04&orientation=landscape',
    author: '穿搭顾问小雅',
    publishDate: '2026-07-20',
    readTime: '7分钟',
    tags: ['脱衣顺序', '衣物保养', '穿搭技巧', '生活常识'],
    featured: true,
    hot: false,
  },
  {
    id: '5',
    slug: 'travel-outfit-packing',
    title: '旅行穿搭：一箱搞定一周造型的打包秘籍',
    excerpt: '行李箱空间有限，如何在旅行中每天穿出不重样的造型？掌握"一衣多穿"的技巧，用一个20寸登机箱装下7天不重复的完美造型。',
    content: [
      { type: 'heading', level: 2, content: '旅行穿搭的挑战' },
      { type: 'paragraph', content: '旅行中最让人头疼的问题之一就是"带什么衣服"。带多了箱子太重，带少了又怕不够穿。其实，只要掌握了科学的打包方法和搭配技巧，一个20寸的登机箱完全可以满足7天的穿搭需求。' },
      { type: 'heading', level: 2, content: '核心原则：选择百搭基础款' },
      { type: 'paragraph', content: '旅行衣物的选择标准只有一个：百搭。建议携带2条裤子（一条牛仔裤、一条阔腿裤或休闲裤）、3件上衣（白T恤、条纹衫、纯色衬衫）、1件外套（风衣或牛仔外套）、1条连衣裙、2双鞋（一双舒适行走鞋、一双稍正式的鞋）。这些基础款之间可以自由组合出至少10套不同的穿搭。' },
      { type: 'heading', level: 2, content: '卷叠法：节省50%空间' },
      { type: 'paragraph', content: '将衣物卷起来而不是折叠，是旅行打包的经典技巧。卷起来的衣物不仅节省空间，还不容易起皱。具体方法：将衣物平铺，从一端紧紧卷到另一端，像卷寿司一样。T恤、裤子、裙子都适合这种卷法。卷好后按照使用频率排列放入行李箱。' },
      { type: 'heading', level: 2, content: '配饰是造型变换的灵魂' },
      { type: 'paragraph', content: '同样的基础款，搭配不同的配饰就能呈现完全不同的风格。建议带2-3条丝巾（可以做头巾、腰带、包饰）、1条百搭项链、1-2对耳环。丝巾占地面积几乎为零，却能让你的造型每天都有新鲜感。' },
    ],
    category: '穿搭搭配',
    categorySlug: 'match',
    coverImage: 'https://readdy.ai/api/search-image?query=Open%20suitcase%20with%20neatly%20packed%20stylish%20clothing%20in%20neutral%20earth%20tones%2C%20travel%20outfit%20planning%2C%20fashion%20blogger%20aesthetic%2C%20warm%20natural%20light%2C%20organized%20packing%20layout%2C%20feminine%20travel%20lifestyle%2C%20cream%20and%20sand%20color%20palette&width=800&height=500&seq=article-travel-05&orientation=landscape',
    author: '旅行博主圈圈',
    publishDate: '2026-07-15',
    readTime: '6分钟',
    tags: ['旅行穿搭', '打包技巧', '一衣多穿', '行李箱收纳'],
    featured: false,
    hot: true,
  },
  {
    id: '6',
    slug: 'homewear-comfort-style',
    title: '居家服的舒适与时尚平衡术：在家也要美美的',
    excerpt: '居家办公和宅家生活成为常态，如何在追求极致舒适的同时，保持精致优雅的形象？这份居家穿搭指南为您找到舒适与时尚的完美平衡。',
    content: [
      { type: 'heading', level: 2, content: '为什么居家穿搭很重要' },
      { type: 'paragraph', content: '很多人在家就随便套一件旧T恤和运动裤，但研究表明，居家着装会直接影响你的心理状态和工作效率。穿得精致得体（即使在家），能让你更专注、更有动力。尤其是在视频会议频繁的今天，上半身的穿搭更是不能马虎。' },
      { type: 'heading', level: 2, content: '居家服面料选择指南' },
      { type: 'paragraph', content: '居家服的首要考虑因素是面料。推荐选择纯棉、莫代尔、竹纤维等天然面料，透气亲肤不闷汗。冬季可以选择羊绒、羊毛混纺的针织面料，保暖又舒适。避免选择化纤含量过高的面料，久穿容易产生静电和不适感。' },
      { type: 'heading', level: 2, content: '居家穿搭的四大风格' },
      { type: 'list', items: ['慵懒法式风：宽松亚麻衬衫 + 棉质阔腿裤，随性又有质感，视频会议时只需整理一下发型即可', '日系清新风：纯色针织开衫 + 棉麻长裙，温柔舒适，适合阅读喝下午茶时穿着', '运动休闲风：高品质瑜伽套装 +  oversize卫衣，方便随时做拉伸运动，保持活力', '简约北欧风：纯色高领打底 + 羊毛混纺长裤，利落不拖沓，居家办公的不二之选'] },
    ],
    category: '穿搭搭配',
    categorySlug: 'match',
    coverImage: 'https://readdy.ai/api/search-image?query=Cozy%20feminine%20home%20wear%20outfit%2C%20woman%20in%20soft%20cashmere%20loungewear%20sitting%20on%20a%20sofa%20with%20warm%20sunlight%2C%20Scandinavian%20living%20room%2C%20comfortable%20yet%20stylish%2C%20cream%20and%20oatmeal%20tones%2C%20lifestyle%20photography%2C%20minimalist%20aesthetic&width=800&height=500&seq=article-home-06&orientation=landscape',
    author: '生活美学博主',
    publishDate: '2026-07-10',
    readTime: '5分钟',
    tags: ['居家穿搭', '舒适时尚', '居家办公', '面料选择'],
    featured: false,
    hot: false,
  },
  {
    id: '7',
    slug: 'date-night-outfit',
    title: '约会穿搭指南：从脱到穿的精致体验',
    excerpt: '约会前的换装环节往往伴随着紧张和期待。本文将为你规划从脱下日常装到穿上约会战袍的完整流程，让你每一步都从容优雅。',
    content: [
      { type: 'heading', level: 2, content: '约会穿搭的心态准备' },
      { type: 'paragraph', content: '约会穿搭最重要的是"做自己"。不要为了迎合对方而穿完全不是自己风格的衣服。穿得舒服、自信，才能展现出最好的状态。如果一条裙子让你随时担心走光或不舒服，那它再好看也不适合约会穿着。' },
      { type: 'heading', level: 2, content: '不同约会场景的穿搭方案' },
      { type: 'list', items: ['咖啡厅约会：针织衫 + A字裙 + 芭蕾平底鞋，温柔知性，轻松不刻意', '餐厅晚餐：丝质吊带裙 + 小西装外套 + 细跟鞋，优雅性感，恰到好处', '户外散步：白色衬衫 + 高腰牛仔裤 + 小白鞋，清爽自然，充满活力', '看电影：oversize卫衣 + 短裙 + 马丁靴，休闲时尚，舒适度满分'] },
      { type: 'heading', level: 2, content: '约会前的换装流程' },
      { type: 'paragraph', content: '理想的约会换装流程是：先从头到脚进行简单的清洁和护肤，然后按照正确的顺序穿好内衣，接着穿好下装，再穿上装，最后套上外套。穿好后在全身镜前做最后的检查，确保每个细节都完美无瑕。整个过程建议留足30-45分钟，不慌不忙才能从容优雅。' },
    ],
    category: '穿搭搭配',
    categorySlug: 'match',
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20checking%20her%20outfit%20in%20a%20full-length%20mirror%2C%20romantic%20date%20night%20preparation%2C%20beautiful%20dress%20with%20soft%20silhouette%2C%20warm%20bedroom%20lighting%2C%20feminine%20fashion%20photography%2C%20champagne%20and%20rose%20gold%20accents%2C%20sophisticated%20aesthetic&width=800&height=500&seq=article-date-07&orientation=landscape',
    author: '约会穿搭顾问',
    publishDate: '2026-07-05',
    readTime: '6分钟',
    tags: ['约会穿搭', '换装流程', '场景穿搭', '精致生活'],
    featured: true,
    hot: false,
  },
  {
    id: '8',
    slug: 'work-to-date-switch',
    title: '高效换装：从办公室到约会的一键切换术',
    excerpt: '下班后直接赴约却没有时间回家换衣服？学会这几招"一键切换"技巧，让你在办公室洗手间就能完成从职场丽人到约会女神的华丽变身。',
    content: [
      { type: 'heading', level: 2, content: '职场到约会的换装挑战' },
      { type: 'paragraph', content: '对于忙碌的都市女性来说，下班后直接赴约是常有的事。但办公室的着装往往偏正式保守，不太适合约会场合。如何在有限的空间和时间内完成风格转换？关键在于"层层叠加"式的穿搭策略和关键单品的替换。' },
      { type: 'heading', level: 2, content: '早上的穿搭准备：层层叠加法' },
      { type: 'paragraph', content: '早上出门时采用"层层叠加"的方式穿搭：最内层穿一件精致的吊带或修身打底衫（约会时可以外露），中间层穿上班的衬衫或针织衫，外层是西装外套或风衣。下装选择一条既可以配衬衫又可以配吊带的百搭裤子或裙子。这样到了下班时间，只需脱下中间层的衬衫，露出精致的内搭，风格就完全不同了。' },
      { type: 'heading', level: 2, content: '关键单品替换清单' },
      { type: 'list', items: ['换一双鞋：把办公室的乐福鞋或低跟鞋换成一双细跟高跟鞋或性感凉鞋', '加一件配饰：戴上一条statement项链或一对闪亮耳环，瞬间提升华丽感', '换一个口红：从白天温柔的豆沙色换成晚上性感的复古红色', '散开头发：如果是盘发或马尾，下班后散开头发，用手指梳理出自然卷度'] },
      { type: 'heading', level: 2, content: '必备的"换装包"清单' },
      { type: 'paragraph', content: '建议在办公室常备一个"换装应急包"，里面放一双备用高跟鞋、一条百搭项链、一支约会色号的口红、小瓶香水、迷你发蜡。这样无论临时有什么约会安排，你都能在10分钟内完成从职场到约会的完美切换。' },
    ],
    category: '日常换装',
    categorySlug: 'daily',
    coverImage: 'https://readdy.ai/api/search-image?query=Stylish%20woman%20transforming%20office%20look%20to%20evening%20outfit%2C%20day%20to%20night%20fashion%20transition%2C%20elegant%20blazer%20with%20silk%20cami%20underneath%2C%20modern%20office%20setting%2C%20fashion%20editorial%2C%20warm%20ambient%20lighting%2C%20sophisticated%20feminine%20style&width=800&height=500&seq=article-switch-08&orientation=landscape',
    author: '效率穿搭师',
    publishDate: '2026-06-28',
    readTime: '7分钟',
    tags: ['高效换装', '职场穿搭', '约会穿搭', '一键切换'],
    featured: false,
    hot: true,
  },
  {
    id: '9',
    slug: 'color-matching-basics',
    title: '色彩搭配基础：打造高级感衣橱的配色法则',
    excerpt: '同样的款式，不同的配色可以呈现出完全不同的质感。掌握基础的色彩搭配原理，让你的每一套穿搭都散发出高级感。',
    content: [
      { type: 'heading', level: 2, content: '色彩是穿搭的灵魂' },
      { type: 'paragraph', content: '在穿搭中，色彩的影响力远超款式。一件剪裁普通的白衬衫配上一条颜色高级的裤子，整体造型就会显得很有质感。反之，再贵的衣服如果颜色搭配不当，也会显得廉价。掌握色彩搭配的基础原理，是实现穿搭进阶的必经之路。' },
      { type: 'heading', level: 2, content: '基础色系：你的衣橱底色' },
      { type: 'paragraph', content: '基础色系包括黑色、白色、灰色、米色、驼色、藏青色。建议衣橱中70%的单品选择基础色系，它们之间可以任意搭配不会出错。剩下的30%留给彩色单品，用来提亮整体造型和展现个人风格。' },
      { type: 'heading', level: 2, content: '四种经典配色方案' },
      { type: 'list', items: ['同色系搭配：选择同一色系不同深浅的颜色进行搭配，如浅驼色+深棕色+米白色，优雅高级不出错', '邻近色搭配：选择色轮上相邻的颜色，如绿色+蓝色、橙色+黄色，和谐又有层次感', '对比色搭配：选择色轮上相对的颜色，如蓝色+橙色、紫色+黄色，视觉冲击力强但需要注意比例', '三色法则：一套穿搭不超过三个主要颜色（不包括黑白灰等中性色），保持简洁不杂乱'] },
      { type: 'heading', level: 2, content: '不同肤色的配色建议' },
      { type: 'paragraph', content: '暖肤色（偏黄）的人适合穿暖色调：驼色、橙色、番茄红、橄榄绿等。冷肤色（偏粉）的人适合穿冷色调：藏青色、紫色、玫红色、薄荷绿等。不确定自己肤色类型的话，在自然光下看手腕内侧血管颜色：偏绿是暖肤色，偏蓝是冷肤色。' },
    ],
    category: '时尚技巧',
    categorySlug: 'tips',
    coverImage: 'https://readdy.ai/api/search-image?query=Fashion%20color%20wheel%20with%20beautiful%20fabric%20swatches%20in%20warm%20earth%20tones%2C%20elegant%20textile%20samples%20arranged%20artistically%2C%20fashion%20design%20studio%20aesthetic%2C%20soft%20natural%20light%2C%20minimalist%20composition%2C%20cream%20background%2C%20luxurious%20texture%20photography&width=800&height=500&seq=article-color-09&orientation=landscape',
    author: '色彩搭配师',
    publishDate: '2026-06-20',
    readTime: '8分钟',
    tags: ['色彩搭配', '高级感穿搭', '配色法则', '肤色分析'],
    featured: false,
    hot: false,
  },
  {
    id: '10',
    slug: 'clothes-folding-techniques',
    title: '衣物折叠整理术：用对方法节省80%衣柜空间',
    excerpt: '同样是叠衣服，为什么别人的衣柜整齐得像精品店，而你的却乱成一团？掌握专业的衣物折叠技巧，让衣柜空间瞬间翻倍。',
    content: [
      { type: 'heading', level: 2, content: '为什么折叠方式很重要' },
      { type: 'paragraph', content: '大多数人的叠衣方式是随意对折，结果就是衣柜里堆满了大小不一、歪歪扭扭的衣物堆。不仅看起来凌乱，取用也不方便（一抽就全塌了）。而专业的折叠方法可以让每件衣物都变成一个规整的"小方块"，直立排列在抽屉中，一目了然、随取随用。' },
      { type: 'heading', level: 2, content: '近藤麻理惠直立折叠法' },
      { type: 'paragraph', content: '日本收纳女王近藤麻理惠的直立折叠法是全球公认最有效的叠衣方法。核心技巧：将衣物叠成一个可以自己"站立"的长方形，然后像书本一样直立排列在抽屉中。这样不仅可以一眼看到所有衣物，取出时也不会弄乱旁边的衣服。' },
      { type: 'heading', level: 2, content: '各类衣物的折叠方法' },
      { type: 'list', items: ['T恤：两边向内折三分之一，下摆向上对折两次，形成一个A4纸大小的长方形，可直立放置', '衬衫：扣好所有扣子，背面朝上，两边向内折，袖子沿肩线折下，下摆向上对折', '裤子：对折使两条裤腿重叠，从裤脚向裤腰方向卷起或折叠，保持裤缝对齐', '裙子：将裙子对折使前后片重叠，根据抽屉高度折叠成相应大小的长方形', '内衣：文胸不可折叠（会破坏杯型），应平铺或使用内衣收纳盒；内裤用"卷寿司"法卷成小卷'] },
      { type: 'heading', level: 2, content: '抽屉分区收纳法' },
      { type: 'paragraph', content: '使用抽屉分隔板将抽屉分成多个区域，不同类别的衣物放在不同区域。例如：一个抽屉用分隔板分成三块，分别放T恤、内搭和打底裤。分隔板的高度应与抽屉一致，确保衣物不会混到一起。' },
    ],
    category: '收纳整理',
    categorySlug: 'storage',
    coverImage: 'https://readdy.ai/api/search-image?query=Neatly%20folded%20clothing%20standing%20upright%20in%20drawer%20organizer%2C%20Marie%20Kondo%20style%20folding%20method%2C%20organized%20wardrobe%20drawer%2C%20color%20coordinated%20t-shirts%20and%20sweaters%2C%20warm%20wooden%20interior%2C%20minimalist%20home%20organization%2C%20clean%20aesthetic&width=800&height=500&seq=article-fold-10&orientation=landscape',
    author: '收纳整理师',
    publishDate: '2026-06-15',
    readTime: '7分钟',
    tags: ['折叠技巧', '衣柜收纳', '近藤麻理惠', '空间优化'],
    featured: false,
    hot: false,
  },
];

export const faqs = [
  {
    question: '每天换衣服应该遵循什么顺序？',
    answer: '建议遵循"由外向内、由上至下"的原则。穿衣时先内后外、先上后下；脱衣时先外后内、先上后下。具体来说，脱衣顺序为：外套 → 配饰 → 上装 → 下装 → 鞋袜 → 内衣。这样能够避免衣物缠绕，保护衣物版型，同时防止拉扯到头发和皮肤。',
  },
  {
    question: '如何快速提升穿搭品味？',
    answer: '提升穿搭品味最快的方法是：第一，建立自己的"胶囊衣橱"，选择30件以内的高品质基础款单品；第二，学会基础色系（黑白灰米驼藏青）之间的搭配；第三，找到2-3位与自己身材气质相近的时尚博主作为参考；第四，每次出门前在全身镜前做最后的整体检查。穿搭品味是可以通过刻意练习快速提升的。',
  },
  {
    question: '换季时衣物如何高效收纳？',
    answer: '换季收纳分四步走：第一步"断舍离"，处理掉一年以上未穿的衣物；第二步"分类"，按材质和厚度分类存放；第三步"清洁"，所有衣物洗净晾干后才能收纳；第四步"封存"，使用透气收纳袋存放，羽绒服和羊毛衫不要用真空压缩袋。收纳后放置在衣柜上层或床底等不常用的空间。',
  },
  {
    question: '旅行时如何用最少衣物搭出最多造型？',
    answer: '核心是"一衣多穿"策略。带2条百搭裤子 + 3件基础上衣 + 1件外套 + 1条连衣裙，这7件单品可以组合出至少10套不同的穿搭。再加2-3条丝巾作为配饰变换造型。所有衣物采用卷叠法打包，可以节省50%的行李箱空间。',
  },
  {
    question: '脱衣服时如何避免损坏衣物？',
    answer: '首先，脱套头衫时从下摆往上拉而非从领口拽；其次，脱连衣裙时从肩部往下褪而非从下摆上拉；第三，脱紧身裤时坐着脱比站着脱更省力且不拉扯面料；第四，脱下后立即挂好或叠好，不要随意堆放。良好的脱衣习惯能让衣物的使用寿命延长30%以上。',
  },
  {
    question: '约会穿搭有哪些需要注意的？',
    answer: '约会穿搭最重要的是"做自己"和"场景适配"。选择让自己感到舒适自信的衣服，同时根据约会场景（咖啡厅、餐厅、户外等）选择合适的风格。建议提前30-45分钟开始换装准备，从容不迫地完成从护肤到穿搭的全流程。配饰方面遵循"少即是多"的原则，一个精致的亮点配饰就足够了。',
  },
];

export const testimonials = [
  {
    name: '林小姐',
    role: '职场白领',
    avatar: 'https://readdy.ai/api/search-image?query=Elegant%20Chinese%20woman%20portrait%20with%20warm%20smile%2C%20professional%20headshot%2C%20soft%20lighting%2C%20neutral%20cream%20background%2C%20natural%20makeup&width=100&height=100&seq=avatar-01&orientation=squarish',
    content: '按照网站上的换装方法调整了早上的穿衣顺序，现在每天至少节省15分钟！更重要的是衣柜变得井井有条，找衣服再也不用翻箱倒柜了。',
  },
  {
    name: '王女士',
    role: '时尚博主',
    avatar: 'https://readdy.ai/api/search-image?query=Stylish%20Chinese%20woman%20portrait%20with%20confident%20expression%2C%20fashion%20headshot%2C%20warm%20soft%20lighting%2C%20cream%20background%2C%20elegant%20minimalist%20style&width=100&height=100&seq=avatar-02&orientation=squarish',
    content: '收藏了这个网站后，我的穿搭灵感源源不断。特别是色彩搭配那篇文章，让我学会了如何用基础款穿出高级感，粉丝都说我最近衣品提升了好多！',
  },
  {
    name: '张小姐',
    role: '旅行爱好者',
    avatar: 'https://readdy.ai/api/search-image?query=Friendly%20Chinese%20woman%20portrait%20with%20gentle%20smile%2C%20casual%20headshot%2C%20soft%20natural%20lighting%2C%20warm%20cream%20background%2C%20approachable%20style&width=100&height=100&seq=avatar-03&orientation=squarish',
    content: '旅行打包那篇文章简直拯救了我！按照卷叠法和一衣多穿的建议，上次去日本7天只带了一个登机箱，每天造型还不重样，朋友们都惊呆了。',
  },
];